import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Plus, Check, X } from 'lucide-react';
import InlineForm from '../components/InlineForm';
import BandInlineForm from '../components/BandInlineForm';
import SortableBand from '../components/SortableBand';

const AdminMilestones = () => {
  const [bands, setBands] = useState([]);
  const [expandedBands, setExpandedBands] = useState({});
  const [expandedQuestionSets, setExpandedQuestionSets] = useState({});
  const [expandedQuestions, setExpandedQuestions] = useState({});
  const [activeForm, setActiveForm] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState('all');
  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(true);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const fetchBands = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/children/global-bands/');
      setBands(response.data);
    } catch (error) {
      console.error('Error fetching bands:', error);
      showNotification('Error fetching bands', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBands();
  }, [fetchBands]);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleBandReorder = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setBands((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        const reordered = arrayMove(items, oldIndex, newIndex);
        return reordered.map((item, index) => ({ ...item, order: index }));
      });
      showNotification('Band order updated');
    }
  };

  const handleQuestionSetReorder = (bandId, event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setBands((bands) =>
        bands.map((band) => {
          if (band.id === bandId) {
            const oldIndex = band.sets.findIndex((qs) => qs.id === active.id);
            const newIndex = band.sets.findIndex((qs) => qs.id === over.id);
            const reordered = arrayMove(band.sets, oldIndex, newIndex);
            return {
              ...band,
              sets: reordered.map((qs, index) => ({ ...qs, order: index }))
            };
          }
          return band;
        })
      );
      showNotification('Question Set order updated');
    }
  };

  const handleQuestionReorder = (bandId, questionSetId, event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setBands((bands) =>
        bands.map((band) => {
          if (band.id === bandId) {
            return {
              ...band,
              sets: band.sets.map((qs) => {
                if (qs.id === questionSetId) {
                  const oldIndex = qs.questions.findIndex((q) => q.id === active.id);
                  const newIndex = qs.questions.findIndex((q) => q.id === over.id);
                  const reordered = arrayMove(qs.questions, oldIndex, newIndex);
                  return {
                    ...qs,
                    questions: reordered.map((q, index) => ({ ...q, order: index }))
                  };
                }
                return qs;
              })
            };
          }
          return band;
        })
      );
      showNotification('Question order updated');
    }
  };

  const handleOptionReorder = (bandId, questionSetId, questionId, event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setBands((bands) =>
        bands.map((band) => {
          if (band.id === bandId) {
            return {
              ...band,
              sets: band.sets.map((qs) => {
                if (qs.id === questionSetId) {
                  return {
                    ...qs,
                    questions: qs.questions.map((q) => {
                      if (q.id === questionId) {
                        const oldIndex = q.options.findIndex((o) => o.id === active.id);
                        const newIndex = q.options.findIndex((o) => o.id === over.id);
                        const reordered = arrayMove(q.options, oldIndex, newIndex);
                        return {
                          ...q,
                          options: reordered.map((o, index) => ({ ...o, order: index }))
                        };
                      }
                      return q;
                    })
                  };
                }
                return qs;
              })
            };
          }
          return band;
        })
      );
      showNotification('Option order updated');
    }
  };

  const saveBand = async (formData) => {
    const data = new FormData();
    for (const key in formData) {
      if (key === 'milestone_image' || key === 'goal_image') {
        if (formData[key] instanceof File) {
          data.append(key, formData[key]);
        } else if (formData[key] === null) {
          data.append(key, '');
        }
      } else if (formData[key] !== null) {
        data.append(key, formData[key]);
      }
    }

    try {
      if (editingItem) {
        const response = await api.patch(`/children/global-bands/${editingItem.id}/`, data, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        setBands((bands) =>
          bands.map((band) => (band.id === editingItem.id ? response.data : band))
        );
        showNotification('Band updated successfully');
      } else {
        const response = await api.post('/children/global-bands/', data, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        setBands([...bands, response.data]);
        showNotification('Band created successfully');
      }
      setActiveForm(null);
      setEditingItem(null);
    } catch (error) {
      console.error('Error saving band:', error);
      showNotification('Error saving band', 'error');
    }
  };

  const saveQuestionSet = async (bandId, formData) => {
    try {
      if (editingItem) {
        const response = await api.patch(`/children/global-question-sets/${editingItem.id}/`, { ...formData, band: bandId });
        setBands((bands) =>
          bands.map((band) =>
            band.id === bandId
              ? { ...band, sets: band.sets.map((qs) => (qs.id === editingItem.id ? response.data : qs)) }
              : band
          )
        );
        showNotification('Question Set updated successfully');
      } else {
        const response = await api.post('/children/global-question-sets/', { ...formData, band: bandId });
        setBands((bands) =>
          bands.map((band) =>
            band.id === bandId ? { ...band, sets: [...band.sets, response.data] } : band
          )
        );
        showNotification('Question Set created successfully');
      }
      setActiveForm(null);
      setEditingItem(null);
    } catch (error) {
      console.error('Error saving question set:', error);
      showNotification('Error saving question set', 'error');
    }
  };

  const saveQuestion = async (bandId, questionSetId, formData) => {
    try {
      if (editingItem) {
        const response = await api.patch(`/children/global-questions/${editingItem.id}/`, { ...formData, band_set: questionSetId });
        setBands((bands) =>
          bands.map((band) =>
            band.id === bandId
              ? {
                  ...band,
                  sets: band.sets.map((qs) =>
                    qs.id === questionSetId
                      ? { ...qs, questions: qs.questions.map((q) => (q.id === editingItem.id ? response.data : q)) }
                      : qs
                  ),
                }
              : band
          )
        );
        showNotification('Question updated successfully');
      } else {
        const response = await api.post('/children/global-questions/', { ...formData, band_set: questionSetId });
        setBands((bands) =>
          bands.map((band) =>
            band.id === bandId
              ? {
                  ...band,
                  sets: band.sets.map((qs) =>
                    qs.id === questionSetId ? { ...qs, questions: [...qs.questions, response.data] } : qs
                  ),
                }
              : band
          )
        );
        showNotification('Question created successfully');
      }
      setActiveForm(null);
      setEditingItem(null);
    } catch (error) {
      console.error('Error saving question:', error);
      showNotification('Error saving question', 'error');
    }
  };

  const saveOption = async (bandId, questionSetId, questionId, formData) => {
    try {
      if (editingItem) {
        const response = await api.patch(`/children/global-answer-options/${editingItem.id}/`, { ...formData, question: questionId });
        setBands((bands) =>
          bands.map((band) =>
            band.id === bandId
              ? {
                  ...band,
                  sets: band.sets.map((qs) =>
                    qs.id === questionSetId
                      ? {
                          ...qs,
                          questions: qs.questions.map((q) =>
                            q.id === questionId
                              ? { ...q, options: q.options.map((o) => (o.id === editingItem.id ? response.data : o)) }
                              : q
                          ),
                        }
                      : qs
                  ),
                }
              : band
          )
        );
        showNotification('Option updated successfully');
      } else {
        const response = await api.post('/children/global-answer-options/', { ...formData, question: questionId });
        setBands((bands) =>
          bands.map((band) =>
            band.id === bandId
              ? {
                  ...band,
                  sets: band.sets.map((qs) =>
                    qs.id === questionSetId
                      ? {
                          ...qs,
                          questions: qs.questions.map((q) =>
                            q.id === questionId ? { ...q, options: [...q.options, response.data] } : q
                          ),
                        }
                      : qs
                  ),
                }
              : band
          )
        );
        showNotification('Option created successfully');
      }
      setActiveForm(null);
      setEditingItem(null);
    } catch (error) {
      console.error('Error saving option:', error);
      showNotification('Error saving option', 'error');
    }
  };

  const deleteItem = async (type, ids) => {
    if (!window.confirm(`Are you sure you want to delete this ${type}?`)) return;

    try {
      let url = '';
      if (type === 'band') {
        url = `/children/global-bands/${ids.bandId}/`;
      } else if (type === 'questionSet') {
        url = `/children/global-question-sets/${ids.questionSetId}/`;
      } else if (type === 'question') {
        url = `/children/global-questions/${ids.questionId}/`;
      } else if (type === 'option') {
        url = `/children/global-answer-options/${ids.optionId}/`;
      }

      await api.delete(url);

      setBands((bands) => {
        if (type === 'band') {
          return bands.filter((b) => b.id !== ids.bandId);
        }
        return bands.map((band) => {
          if (band.id === ids.bandId) {
            if (type === 'questionSet') {
              return { ...band, sets: band.sets.filter((qs) => qs.id !== ids.questionSetId) };
            }
            return {
              ...band,
              sets: band.sets.map((qs) => {
                if (qs.id === ids.questionSetId) {
                  if (type === 'question') {
                    return { ...qs, questions: qs.questions.filter((q) => q.id !== ids.questionId) };
                  }
                  return {
                    ...qs,
                    questions: qs.questions.map((q) => {
                      if (q.id === ids.questionId) {
                        return { ...q, options: q.options.filter((o) => o.id !== ids.optionId) };
                      }
                      return q;
                    }),
                  };
                }
                return qs;
              }),
            };
          }
          return band;
        });
      });
      showNotification(`${type} deleted successfully`);
    } catch (error) {
      console.error(`Error deleting ${type}:`, error);
      showNotification(`Error deleting ${type}`, 'error');
    }
  };

  const toggleActive = (type, ids) => {
    setBands((bands) => {
      if (type === 'band') {
        return bands.map((b) => (b.id === ids.bandId ? { ...b, is_active: !b.is_active } : b));
      }
      return bands.map((band) => {
        if (band.id === ids.bandId) {
          if (type === 'questionSet') {
            return {
              ...band,
              sets: band.sets.map((qs) =>
                qs.id === ids.questionSetId ? { ...qs, is_active: !qs.is_active } : qs
              )
            };
          }
          return {
            ...band,
            sets: band.sets.map((qs) => {
              if (qs.id === ids.questionSetId) {
                if (type === 'question') {
                  return {
                    ...qs,
                    questions: qs.questions.map((q) =>
                      q.id === ids.questionId ? { ...q, is_active: !q.is_active } : q
                    )
                  };
                }
                return {
                  ...qs,
                  questions: qs.questions.map((q) => {
                    if (q.id === ids.questionId) {
                      return {
                        ...q,
                        options: q.options.map((o) =>
                          o.id === ids.optionId ? { ...o, is_active: !o.is_active } : o
                        )
                      };
                    }
                    return q;
                  })
                };
              }
              return qs;
            })
          };
        }
        return band;
      });
    });
    showNotification(`${type} status toggled`);
  };

  const handleExport = async () => {
    try {
      const response = await api.get('/children/global-bands/export_milestones/');
      const data = JSON.stringify(response.data, null, 2);
      const blob = new Blob([data], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'milestones.json';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      showNotification('Milestones exported successfully');
    } catch (error) {
      console.error('Error exporting milestones:', error);
      showNotification('Error exporting milestones', 'error');
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      await api.post('/children/global-bands/import_milestones/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      fetchBands();
      showNotification('Milestones imported successfully');
    } catch (error) {
      console.error('Error importing milestones:', error);
      showNotification('Error importing milestones', 'error');
    }
  };

  const filteredBands = bands.filter((band) => {
    const matchesSearch = band.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      band.code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterActive === 'all' || (filterActive === 'active' ? band.is_active : !band.is_active);
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-6">
      {notification && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-4 rounded-xl shadow-2xl animate-slide-in ${
          notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'
        } text-white font-semibold flex items-center gap-2`}>
          {notification.type === 'success' ? <Check size={20} /> : <X size={20} />}
          {notification.message}
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-[#1e3a5f] to-[#2a4b73] bg-clip-text text-transparent">
                Milestone Builder
              </h1>
              <p className="text-gray-600 mt-2">Create and manage developmental milestones with drag & drop</p>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => { setActiveForm('band'); setEditingItem(null); }}
                className="px-6 py-3 bg-gradient-to-r from-[#1e3a5f] to-[#2a4b73] text-white rounded-xl font-semibold hover:shadow-lg transition-all flex items-center gap-2"
              >
                <Plus size={20} />
                Add New Band
              </button>
              <button
                onClick={handleExport}
                className="px-6 py-3 bg-gray-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all flex items-center gap-2"
              >
                Export JSON
              </button>
              <button
                onClick={() => document.getElementById('import-file').click()}
                className="px-6 py-3 bg-gray-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all flex items-center gap-2"
              >
                Import JSON
              </button>
              <input type="file" id="import-file" className="hidden" onChange={handleImport} accept=".json" />
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search bands..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]"
              />
            </div>
            <div className="flex gap-2">
              {['all', 'active', 'inactive'].map((filter) => (
                <button
                  key={filter}
                  onClick={() => setFilterActive(filter)}
                  className={`px-6 py-3 rounded-xl font-semibold transition-all ${
                    filterActive === filter
                      ? 'bg-[#1e3a5f] text-white shadow-md'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {filter.charAt(0).toUpperCase() + filter.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {activeForm === 'band' && (
          <InlineForm
            title={editingItem ? 'Edit Band' : 'Add New Band'}
            onCancel={() => { setActiveForm(null); setEditingItem(null); }}
          >
            <BandInlineForm
              band={editingItem}
              onSave={saveBand}
              onCancel={() => { setActiveForm(null); setEditingItem(null); }}
            />
          </InlineForm>
        )}

        {loading ? (
          <div className="text-center py-12">
            <p className="text-lg font-semibold text-gray-600">Loading milestones...</p>
          </div>
        ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleBandReorder}>
          <SortableContext items={filteredBands.map((b) => b.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-4">
              {filteredBands.map((band) => (
                <SortableBand
                  key={band.id}
                  band={band}
                  expanded={expandedBands[band.id]}
                  onToggle={() => setExpandedBands({ ...expandedBands, [band.id]: !expandedBands[band.id] })}
                  onEdit={() => { setEditingItem(band); setActiveForm('band'); }}
                  onDelete={() => deleteItem('band', { bandId: band.id })}
                  onToggleActive={() => toggleActive('band', { bandId: band.id })}
                  onAddQuestionSet={() => { setActiveForm(`questionSet-${band.id}`); setEditingItem(null); }}
                  expandedQuestionSets={expandedQuestionSets}
                  setExpandedQuestionSets={setExpandedQuestionSets}
                  expandedQuestions={expandedQuestions}
                  setExpandedQuestions={setExpandedQuestions}
                  handleQuestionSetReorder={handleQuestionSetReorder}
                  handleQuestionReorder={handleQuestionReorder}
                  handleOptionReorder={handleOptionReorder}
                  activeForm={activeForm}
                  setActiveForm={setActiveForm}
                  editingItem={editingItem}
                  setEditingItem={setEditingItem}
                  saveQuestionSet={saveQuestionSet}
                  saveQuestion={saveQuestion}
                  saveOption={saveOption}
                  deleteItem={deleteItem}
                  toggleActive={toggleActive}
                  sensors={sensors}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
        )}

        {!loading && filteredBands.length === 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <div className="text-gray-400 mb-4">
              <svg className="w-24 h-24 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-700 mb-2">No Bands Found</h3>
            <p className="text-gray-500">Start by creating your first developmental band</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminMilestones;
